import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { mqMin } from 'utils/responsive';

const Container = styled.section`
    padding: 45px 0 52px 0;
    ${mqMin('NARROW')} {
        padding: 78px 0 70px 0;
        max-width: 900px;
        margin: 0 auto;
    }
`;

const H2 = styled.h2`
    text-align: center;
    font-size: 1.556rem;
    margin-bottom: 28px;

    ${mqMin('SP')} {
        font-size: 2rem;
    }
    ${mqMin('NARROW')} {
        text-align: center;
        font-size: 1.556rem;
        margin-bottom: 28px;
    }
`;

const QAContainer = styled.section`
    width: 88.37209302325581%;
    border-top: solid 2px var(--dark-blue);
    border-bottom: solid 2px var(--dark-blue);
    margin: 0 auto;

    ${mqMin('NARROW')} {
        padding: 20 0;
    }
`;

const Item = styled.section`
    width: 100%;
    border-bottom: solid 1px #dadada;
`;

const QButton = styled.div`
    width: 100%;
    position: relative;
    background-color: #fff;
    color: var(--dark-blue);
    padding: 0;
    cursor: pointer;
`;

const QTextContainer = styled.div`
    display: flex;
    align-items: center;
    h3 {
        width: calc(100% - 24px);
        padding: 20px;
        text-align: left;
        font-size: 0.7rem;
        line-height: 1.7em;
        letter-spacing: 0.1em;

        ${mqMin('SP')} {
            font-size: 1.4rem;
        }

        ${mqMin('NARROW')} {
            font-size: 0.889rem;
        }
    }
`;

const QMark = styled.div<{ isOpen: boolean }>`
    position: relative;
    display: block;
    width: 20px;
    height: 20px;
    span:nth-of-type(1) {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        display: block;
        width: 100%;
        height: 2px;
        background-color: var(--dark-blue);
    }
    span:nth-of-type(2) {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        display: block;
        width: 2px;
        height: 100%;
        background-color: var(--dark-blue);
        transition: transform 0.2s;
        transform: ${({ isOpen }) => (isOpen ? 'rotate(90deg)' : 'rotate(0deg)')};
    }
`;

const AItem = styled.div<{ isOpen: boolean }>`
    width: 100%;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')}; /* 仮の最大値 */
`;

const AItemText = styled.div`
    padding: 0 20px 20px 20px;
    text-align: left;
    line-height: 1.7em;
    letter-spacing: 0.1em;
    font-size: 0.778rem;

    ${mqMin('SP')} {
        font-size: 1.4rem;
    }

    ${mqMin('NARROW')} {
        font-size: 0.778rem;
    }
`;

const OrangeText = styled.div`
    color: var(--orange);
`;

const faqData = [
    {
        question: 'どのような会社を買えば、うまくいくのでしょうか？',
        answer: (
            <>
                <OrangeText>「安い会社」より「自社とのシナジーがある会社」を選ぶことが成功の鍵です。</OrangeText>
                <div>
                    業種の近さ、顧客基盤、人材、技術などの面で自社との補完関係があるかを見極めましょう。
                    <br />
                    戦略との整合性が明確な案件ほど、PMI（統合）もうまく進みます。
                </div>
            </>
        ),
    },
    {
        question: 'どこまで情報を開示してもらえるのですか？',
        answer: (
            <>
                <OrangeText>初期段階では、匿名資料（ノンネーム）での概要提示が主流です。</OrangeText>
                <div>
                    NDA（秘密保持契約）を締結した後、社名・財務情報・組織構成・事業詳細などのフルパッケージ資料の提供へ進みます。
                    <br />
                    開示範囲については、都度売り手との調整を行うケースが多いです。
                </div>
            </>
        ),
    },
    {
        question: '仲介会社によって、提案内容は違うのですか？',
        answer: (
            <>
                <OrangeText>売り案件は専任契約が多いので、仲介会社によって提案される案件が異なります。</OrangeText>
                <div>自社が希望する案件を持っている仲介会社を見つけることが、M&Aの実行に向けての第一歩です。</div>
            </>
        ),
    },
    {
        question: 'M&Aを進めるベストなタイミングはありますか？',
        answer: (
            <>
                <OrangeText>
                    「資金があるとき」「人材が足りないとき」「成長の壁を感じたとき」などが一つの目安です。
                </OrangeText>
                <div>
                    また、市場環境や事業フェーズによって「今買うべきか」の判断は変わります。
                    <br />
                    まずは1社と出会ってみることで、自社にとっての最適なタイミングが見えてきます。
                </div>
            </>
        ),
    },
    {
        question: '交渉や契約はどうやって進めればいいですか？',
        answer: (
            <>
                <OrangeText>
                    専門の仲介会社やアドバイザーが間に入り、条件交渉・書類作成・スケジュール管理まで支援します。
                </OrangeText>
                <div>自社で全て行う必要はなく、M&A初心者でもプロと連携しながら進めることが可能です。</div>
            </>
        ),
    },
];

export const QA: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const answerRefs = useRef<(HTMLDivElement | null)[]>([]);

    const toggleFAQ = (index: number) => {
        setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
    };

    useEffect(() => {
        answerRefs.current.forEach((ref, index) => {
            if (ref) {
                if (openIndex === index) {
                    ref.style.maxHeight = ref.scrollHeight + 'px';
                } else {
                    ref.style.maxHeight = '0';
                }
            }
        });
    }, [openIndex]);

    return (
        <Container id="qa">
            <H2 className="fadein fadein_y">よくあるご質問</H2>
            <QAContainer className="fadein fadein_y">
                {faqData.map((faq, index) => (
                    <Item key={index}>
                        <QButton className="topFaq_item_q" onClick={() => toggleFAQ(index)}>
                            <QTextContainer className="topFaq_item_q_text">
                                <h3>{faq.question}</h3>
                                <QMark isOpen={openIndex === index}>
                                    <span className="topFaq_item_q_mark_line01"></span>
                                    <span className="topFaq_item_q_mark_line02"></span>
                                </QMark>
                            </QTextContainer>
                        </QButton>
                        <AItem ref={(el) => (answerRefs.current[index] = el)} isOpen={openIndex === index}>
                            <AItemText>{faq.answer}</AItemText>
                        </AItem>
                    </Item>
                ))}
            </QAContainer>
        </Container>
    );
};
